import {
  CertificadoRecebiveisApiResponse,
  CadastroCertificadoRecebiveisApiRequest,
  InstrumentoFinanceiro,
  TipoRegistroInstrumentoFinanceiro,
  ReservarUltimoNumeroDaEmissaoRequest,
} from 'common/genericTypes';
import Formulario from 'pages/certificadoRecebiveis/registro/formulario';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { RootState } from 'store';
import {
  resetDetails,
  updateCRCaracteristicas,
  updateField,
} from 'store/modules/certificadoRecebiveis/caracteristicas';
import {
  resetCriteriosRemuneracao,
  updateCriteriosRemuneracao,
} from 'store/modules/certificadoRecebiveis/criteriosRemuneracao';
import { resetEventos, updateEventos } from 'store/modules/certificadoRecebiveis/eventos';
import { resetDocuments, updateDocuments } from 'store/modules/certificadoRecebiveis/uploadDocumentos';
import { updateId, resetEmissor } from 'store/modules/certificadoRecebiveis/emissor';
import { UploadDocResponse } from 'store/modules/models/uploadDocResponse';
import CertificadoRecebiveisParser from 'utils/parsers/certificadoRecebiveisParser';
import { addAlert } from 'store/modules/snackBar';
import { highlightErrors, typeOfRegister } from 'store/modules/certificadoRecebiveis';

type ComponentParams = { certificadoRecebiveisId?: string };
type ComponentState = { crRascunho?: CertificadoRecebiveisApiResponse; acao?: TipoRegistroInstrumentoFinanceiro };

type Props = RouteComponentProps<ComponentParams, {}, ComponentState>;

const CertificadoRecebiveis: React.FC<Props> = (props) => {
  const { certificadoRecebiveisId } = props.match.params;
  const crRascunho = props.location.state?.crRascunho;

  const history = useHistory();
  const dispatch = useDispatch();
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);

  const caracteristicas = useSelector((state: RootState) => state.certificadoRecebiveisCadastro.caracteristicas);
  const criteriosRemuneracao = useSelector(
    (state: RootState) => state.certificadoRecebiveisCadastro.criteriosRemuneracao
  );
  const eventos = useSelector((state: RootState) => state.certificadoRecebiveisCadastro.eventos);
  const uploadDocumentos = useSelector((state: RootState) => state.certificadoRecebiveisCadastro.uploadDocumentos);
  const emissor = useSelector((state: RootState) => state.certificadoRecebiveisCadastro.emissor);
  const certificadoRecebiveisApi = useSelector((state: RootState) => state.certificadoRecebiveisApi);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);

  const [codigo, setCodigo] = useState<string>('');
  const [typeOfFormulario, setTypeOfFormulario] = useState<TipoRegistroInstrumentoFinanceiro>(TipoRegistroInstrumentoFinanceiro.Cadastro);

  const submitPut = async (
    certificadoRecebiveisId: string,
    certificadoRecebiveis: CadastroCertificadoRecebiveisApiRequest
  ) => {
    let result = { codigo: '', ticket: '' };
    try {
      const res = await certificadoRecebiveisApi.putCertificadoRecebiveis(dispatch, certificadoRecebiveisId, certificadoRecebiveis, true);
      result = { codigo: res.codigo, ticket: res.ticket };
    } catch (error) { }
    return result;
  };

  const submitPost = async (): Promise<CertificadoRecebiveisApiResponse> => {
    try {
      const certificadoRecebiveis: CadastroCertificadoRecebiveisApiRequest =
        CertificadoRecebiveisParser.stateToCadastroCertificadoRecebiveisApiRequest(
          caracteristicas,
          criteriosRemuneracao,
          eventos,
          uploadDocumentos,
          emissor
        );
      return await certificadoRecebiveisApi.postCertificadoRecebiveis(dispatch, certificadoRecebiveis, true);
    } catch (error) {
      return {} as CertificadoRecebiveisApiResponse;
    }
  };

  const submitDocument = async (
    certificadoRecebiveisId: string,
    emissionType: string,
    file: File
  ): Promise<UploadDocResponse[]> => {
    try {
      return certificadoRecebiveisApi.putUploadDoc(dispatch, certificadoRecebiveisId, emissionType, file, true);
    } catch (error) {
      return [];
    }
  };

  const handleEditSubmit = async () => {
    if (!certificadoRecebiveisId) return { codigo: '', ticket: '' };

    const certificadoRecebiveis: CadastroCertificadoRecebiveisApiRequest =
      CertificadoRecebiveisParser.stateToCadastroCertificadoRecebiveisApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );

    const response = await submitPut(certificadoRecebiveisId, certificadoRecebiveis);

    for (const document of uploadDocumentos.documents) {
      if (document.file) {
        const uploadResponse = await submitDocument(certificadoRecebiveisId, document.type, document.file);
        if (!uploadResponse || uploadResponse.length === 0) return { codigo: '', ticket: '' };
      }
    }

    if (caracteristicas.id) {
      for (const documentToDelete of uploadDocumentos.documentsToDelete) {
        documentToDelete.remoteFileInfo?.id &&
          (await certificadoRecebiveisApi.deleteUploadDoc(
            dispatch,
            certificadoRecebiveisId,
            documentToDelete.remoteFileInfo.id,
            true
          ));
      }
    }

    return response;
  };

  const handleRegisterSubmit = async () => {
    const uploadedCr = await submitPost();

    if (uploadedCr.id) {
      for (const document of uploadDocumentos.documents) {
        if (document.file) {
          await submitDocument(uploadedCr.certificadoRecebiveisId, document.type, document.file);
        }
      }
    }

    return { codigo: '', ticket: uploadedCr.ticket };
  };

  const handleAditamentoSubmit = async (): Promise<{ codigo: string, ticket: string } | undefined> => {
    try {
      if (codigo) {
        return { codigo, ticket: '' };
      } else throw 'ID do CR não identificado';
    } catch (error) {
      console.error(error);
    }
  };

  const handleReturnToList = () => {
    history.push('/solicitacoes');
  };

  const handleSalvarRascunho = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return '';
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return '';
    }
    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.CertificadoRecebiveis,
        externalId: participanteId,
      };
      
      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return '';
    }
    let draftId = '';
    try {
      const rascunho: CadastroCertificadoRecebiveisApiRequest =
        CertificadoRecebiveisParser.stateToCadastroCertificadoRecebiveisApiRequest(
          caracteristicas,
          criteriosRemuneracao,
          eventos,
          uploadDocumentos,
          emissor
        );

      const documents = uploadDocumentos.documents
        .filter((doc) => !!doc.file)
        .map((doc) => ({ file: doc.file!, emissionType: doc.type }));

      const result = caracteristicas.draftId
        ? await instrumentoApi.putRascunho(
          dispatch,
          InstrumentoFinanceiro.CertificadoRecebiveis,
          caracteristicas.draftId,
          rascunho,
          documents,
          true
        )
        : await instrumentoApi.postRascunho(
          dispatch,
          InstrumentoFinanceiro.CertificadoRecebiveis,
          rascunho,
          documents,
          true
        );

      setCodigo(result.codigo);
      draftId = result.draftId;
      dispatch(updateField({ name: 'draftId', value: draftId }));

      dispatch(addAlert({ message: `Rascunho salvo com sucesso. Código do Instrumento Financeiro ${result.codigo}`, severity: 'success' }));
    } catch (error) { }
    return draftId;
  };

  const defineTypeOfRegister = async (): Promise<void> => {
    if (certificadoRecebiveisId) {
      if (props.location.state?.acao) {
        setTypeOfFormulario(props.location.state.acao);
        dispatch(typeOfRegister(props.location.state.acao));
      }
    } else {
      setTypeOfFormulario(TipoRegistroInstrumentoFinanceiro.Cadastro);
      dispatch(typeOfRegister(TipoRegistroInstrumentoFinanceiro.Cadastro));
    }
  };

  useEffect(() => {
    dispatch(resetEmissor());
    dispatch(resetDetails());
    dispatch(resetCriteriosRemuneracao());
    dispatch(resetEventos());
    dispatch(resetDocuments());
    dispatch(highlightErrors(false));
  }, []);

  useEffect(() => {
    if (certificadoRecebiveisId) {
      certificadoRecebiveisApi
        .getCertificadoRecebiveisDetalhes(dispatch, certificadoRecebiveisId, true)
        .then((response) => {
          if (response) {
            setCodigo(response.codigo);
            dispatch(updateId(response.emissorId ?? ''));
            dispatch(updateCRCaracteristicas(CertificadoRecebiveisParser.apiResponseToCaracteristicasState(response)));
            dispatch(
              updateCriteriosRemuneracao(CertificadoRecebiveisParser.apiResponseToCriteriosRemuneracaoState(response))
            );
            dispatch(updateEventos(CertificadoRecebiveisParser.apiResponseToEventosState(response)));
            dispatch(
              updateDocuments(CertificadoRecebiveisParser.apiResponseToCertificadoRecebiveisDocumentsState(response))
            );
          }
        })
        .catch(() => { });
    } else if (crRascunho) {
      dispatch(updateId(crRascunho.emissorId ?? ''));
      dispatch(
        updateCRCaracteristicas(CertificadoRecebiveisParser.apiResponseToCaracteristicasState(crRascunho, false))
      );
      dispatch(
        updateCriteriosRemuneracao(CertificadoRecebiveisParser.apiResponseToCriteriosRemuneracaoState(crRascunho))
      );
      dispatch(updateEventos(CertificadoRecebiveisParser.apiResponseToEventosState(crRascunho)));
      dispatch(
        updateDocuments(CertificadoRecebiveisParser.apiResponseToCertificadoRecebiveisDocumentsState(crRascunho))
      );
    }
    defineTypeOfRegister();
  }, [certificadoRecebiveisApi, certificadoRecebiveisId, crRascunho, dispatch]);

  const component = () => {
    switch (typeOfFormulario) {
      case TipoRegistroInstrumentoFinanceiro.Cadastro:
        return (
          <div>
            <Formulario
              certificadoRecebiveisId = {certificadoRecebiveisId}
              title={`Cadastro CR`}
              handleSubmit={handleRegisterSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={false}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Edicao:
        return (
          <div>
            <Formulario
              certificadoRecebiveisId = {certificadoRecebiveisId}
              title={`Editar CR`}
              handleSubmit={handleEditSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={true}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.EdicaoEmitido:
        return (
          <div>
            <Formulario
              certificadoRecebiveisId = {certificadoRecebiveisId}
              title={`Editar ${codigo}`}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={true}
              codigo={codigo}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Aditamento:
        return (
          <div>
            <Formulario
              certificadoRecebiveisId = {certificadoRecebiveisId}
              title={`Aditar CR ${codigo}`}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={true}
              codigo={codigo}
            />
          </div>
        );
      default:
        return <p>Loading</p>
    }
  };
  return component();
};

export default CertificadoRecebiveis;
